'use client';

import { faHeart } from '@fortawesome/pro-regular-svg-icons';
import TooltipProductCard from '@/components/common/tooltip-product-card/tooltip-product-card';
import { Badge, Button, Icon, IconButton, Tooltip } from '@/components/ui';
import useFavorites from '@/hooks/use-favorites/use-favorites';
import useHeaderContent from '@/hooks/use-header-content';
import useNavigate from '@/hooks/use-navigate';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { cn } from '@/utils';
import type { HeaderToolbarVariant } from './types';

export interface FavoritesButtonProps {
  variant?: HeaderToolbarVariant;
  /** Smaller icon-only control for the mobile menu drawer footer. */
  compact?: boolean;
}

const FavoritesButtonPreview = ({ variant = 'primary', compact = false }: FavoritesButtonProps) => {
  const resolveThemeColor = useResolveThemeColor();
  const headerContent = useHeaderContent();
  const isSecondary = variant === 'secondary';
  const desktopBtnClass = isSecondary
    ? 'hidden h-auto px-0.5 pt-4 pb-3 @xl:block'
    : 'hidden h-auto px-0.5 pt-4 pb-3 @lg:block';
  const mobileBtnClass = isSecondary
    ? 'flex h-auto p-1.5 @xl:hidden'
    : 'flex h-auto p-1.5 @lg:hidden';
  const toolbarTextResolved = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');
  const iconColorStyle = { color: toolbarTextResolved };

  if (compact) {
    return (
      <IconButton
        aria-label="Favorites"
        variant="light"
        className="h-auto min-h-0 overflow-visible p-1"
        size="sm"
        isDisabled
        style={iconColorStyle}
      >
        <Badge variant="solid" content={0} size="sm">
          <Icon icon={faHeart} size="sm" style={iconColorStyle} />
        </Badge>
      </IconButton>
    );
  }

  return (
    <>
      <Button
        aria-label="Favorites"
        variant="light"
        className={cn(desktopBtnClass, 'overflow-visible')}
        size="sm"
        isDisabled
        style={iconColorStyle}
      >
        <Badge content={0} className="absolute -top-1 -right-1">
          <div className="flex flex-col gap-2">
            <Icon icon={faHeart} size="xl" style={iconColorStyle} />
          </div>
        </Badge>
      </Button>
      <IconButton
        aria-label="Favorites"
        variant="light"
        className={cn(mobileBtnClass, 'overflow-visible')}
        size="lg"
        isDisabled
        style={iconColorStyle}
      >
        <Badge variant="solid" content={0}>
          <Icon icon={faHeart} size="lg" style={iconColorStyle} />
        </Badge>
      </IconButton>
    </>
  );
};

const FavoritesButtonLive = ({ variant = 'primary', compact = false }: FavoritesButtonProps) => {
  const resolveThemeColor = useResolveThemeColor();
  const { t } = useTranslation();
  const { favorites, isLoadingFavorites } = useFavorites();
  const navigate = useNavigate();
  const headerContent = useHeaderContent();

  const isSecondary = variant === 'secondary';
  const tooltipDesktopClass = isSecondary
    ? 'hidden overflow-hidden @xl:block'
    : 'hidden overflow-hidden @lg:block';
  const desktopBtnClass = isSecondary
    ? 'hidden h-auto px-0.5 pt-4 pb-3 @xl:block'
    : 'hidden h-auto px-0.5 pt-4 pb-3 @lg:block';
  const mobileBtnClass = isSecondary
    ? 'flex h-auto p-1.5 @xl:hidden'
    : 'flex h-auto p-1.5 @lg:hidden';

  const toolbarTextResolved = resolveThemeColor(headerContent.mainRowTextColor ?? 'foreground');
  const iconColorStyle = { color: toolbarTextResolved };
  const favoritesCount = favorites?.products?.length ?? 0;

  if (compact) {
    return (
      <IconButton
        aria-label="Favorites"
        onClick={() => navigate('/favorites')}
        variant="light"
        className="h-auto min-h-0 overflow-visible p-1"
        size="sm"
        isDisabled={false}
        style={iconColorStyle}
      >
        <Badge variant="solid" content={favoritesCount} size="sm" isInvisible={isLoadingFavorites}>
          <Icon icon={faHeart} size="sm" style={iconColorStyle} />
        </Badge>
      </IconButton>
    );
  }

  return (
    <>
      <Tooltip
        className={tooltipDesktopClass}
        classNames={{ content: 'p-2' }}
        shadow="lg"
        showArrow
        delay={300}
        content={
          <div className="flex max-h-96 w-[400px] flex-col overflow-hidden">
            <div className="flex scrollbar-thin flex-col overflow-y-auto">
              {favorites?.products.map(prod => (
                <TooltipProductCard key={prod.name} type="favorites" product={prod} />
              ))}
            </div>
            <div className="border-t border-gray-200 p-4">
              <Button
                onClick={() => navigate('/favorites')}
                className="block w-full text-white"
                style={{ backgroundColor: resolveThemeColor('primary') }}
              >
                {t?.common.layout.seeMyFavorites}
              </Button>
            </div>
          </div>
        }
      >
        <Button
          aria-label="Favorites"
          onClick={() => navigate('/favorites')}
          variant="light"
          className={cn(desktopBtnClass, 'overflow-visible')}
          size="sm"
          style={iconColorStyle}
        >
          <Badge
            content={favoritesCount}
            className="absolute -top-1 -right-1"
            isInvisible={isLoadingFavorites}
          >
            <div className="flex flex-col gap-2">
              <Icon icon={faHeart} size="xl" style={iconColorStyle} />
            </div>
          </Badge>
        </Button>
      </Tooltip>
      <IconButton
        aria-label="Favorites"
        onClick={() => navigate('/favorites')}
        variant="light"
        className={cn(mobileBtnClass, 'overflow-visible')}
        size="lg"
        isDisabled={false}
        style={iconColorStyle}
      >
        <Badge variant="solid" content={favoritesCount} isInvisible={isLoadingFavorites}>
          <Icon icon={faHeart} size="lg" style={iconColorStyle} />
        </Badge>
      </IconButton>
    </>
  );
};

const FavoritesButton = (props: FavoritesButtonProps) => {
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  if (isStudioPreview) {
    return <FavoritesButtonPreview {...props} />;
  }
  return <FavoritesButtonLive {...props} />;
};

export default FavoritesButton;
